import { useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import { publicApiClient, publicTermsUrl } from '@/api/public'
import type { PublicBookingRequest } from '@/api/public'
import { CalendarDays, Clock, MapPin, Users, ArrowLeft, Globe } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { formatPriceLabel, calculateTotal } from '@/lib/pricing'
import { moneyFor } from '@/lib/money'
import { datesFor } from '@/lib/dates'

function isColourDark(hex: string): boolean {
  try {
    const r = parseInt(hex.slice(1, 3), 16)
    const g = parseInt(hex.slice(3, 5), 16)
    const b = parseInt(hex.slice(5, 7), 16)
    return (r * 299 + g * 587 + b * 114) / 1000 < 128
  } catch { return true }
}

type AttendeeRow = { fullName: string; email: string; companyName: string; marketingConsent: boolean }
const emptyAttendee = (): AttendeeRow => ({ fullName: '', email: '', companyName: '', marketingConsent: false })

export default function PublicSessionDetailPage() {
  const { slug, sessionId } = useParams<{ slug: string; sessionId: string }>()
  const navigate = useNavigate()

  const { data, isLoading, isError } = useQuery({
    queryKey: ['public-session', slug, sessionId],
    queryFn: () => publicApiClient.getPublicSession(slug!, sessionId!).then(r => r.data),
    enabled: !!slug && !!sessionId,
  })
  const { fmt: fmtMoney } = moneyFor(data?.currency)
  const { formatDate, formatTime } = datesFor(data?.region, data?.timeZoneId)
  const fmtDate = (iso: string) => {
    return formatDate(iso, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
  }
  const fmtTime = (iso: string) => {
    return formatTime(iso)
  }

  const [contactName, setContactName] = useState('')
  const [contactEmail, setContactEmail] = useState('')
  const [contactPhone, setContactPhone] = useState('')
  const [attendees, setAttendees] = useState<AttendeeRow[]>([emptyAttendee()])
  const [notes, setNotes] = useState('')
  const [termsAccepted, setTermsAccepted] = useState(false)

  const setPlaces = (n: number) => {
    setAttendees(prev => {
      if (n === prev.length) return prev
      if (n > prev.length) return [...prev, ...Array.from({ length: n - prev.length }, emptyAttendee)]
      return prev.slice(0, n)
    })
  }

  const updateAttendee = (i: number, patch: Partial<AttendeeRow>) =>
    setAttendees(prev => prev.map((a, idx) => idx === i ? { ...a, ...patch } : a))

  const bookMutation = useMutation({
    mutationFn: (req: PublicBookingRequest) =>
      publicApiClient.createPublicBooking(slug!, sessionId!, req),
    // Pay-to-book returns a Checkout URL — send the booker straight there.
    // Otherwise go to the confirmation page (which offers payment for priced sessions).
    onSuccess: (res) => {
      if (res.data.checkoutUrl) window.location.href = res.data.checkoutUrl
      else navigate(`/sessions/${slug}/bookings/${res.data.bookingId}`)
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    bookMutation.mutate({
      contactName,
      contactEmail,
      contactPhone,
      attendees: attendees.map(a => ({
        fullName: a.fullName,
        email: a.email,
        companyName: a.companyName || null,
        marketingConsent: a.marketingConsent,
      })),
      notes: notes || null,
      termsAccepted,
    })
  }

  if (isLoading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
    </div>
  )

  if (isError || !data) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <p className="text-lg font-semibold text-slate-700">Session not found</p>
        <Link to={`/sessions/${slug}`} className="text-sm text-indigo-500 mt-2 block">Back to all sessions</Link>
      </div>
    </div>
  )

  const { session, tenantName, tenantLogoUrl, primaryColour, termsVersion } = data
  const requiresTerms = termsVersion > 0
  const fullyBooked = session.availablePlaces === 0

  const hasPrimary = !!primaryColour
  const darkBg = hasPrimary ? isColourDark(primaryColour!) : false
  const headerStyle = hasPrimary ? { backgroundColor: primaryColour! } : undefined
  const headerClass = hasPrimary ? '' : 'bg-white border-b border-slate-200'
  const nameClass = hasPrimary ? (darkBg ? 'text-white' : 'text-slate-900') : 'text-slate-800'

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className={headerClass} style={headerStyle}>
        <div className="max-w-3xl mx-auto px-4 py-5 flex items-center gap-4">
          {tenantLogoUrl && (
            <img src={tenantLogoUrl} alt={tenantName} className="h-8 w-auto object-contain" />
          )}
          <span className={`font-semibold ${nameClass}`}>{tenantName}</span>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        <Link to={`/sessions/${slug}`} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700">
          <ArrowLeft size={15} /> All sessions
        </Link>

        {/* Session info */}
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <div className="flex items-start justify-between gap-4 mb-4">
            <h1 className="text-xl font-bold text-slate-900">{session.courseName}</h1>
            {formatPriceLabel(session.pricingModel, session.price, session.flatFeeAmount, session.flatFeeCapacity, fmtMoney) && (
              <span className="text-2xl font-bold text-slate-900 shrink-0">
                {formatPriceLabel(session.pricingModel, session.price, session.flatFeeAmount, session.flatFeeCapacity, fmtMoney)}
              </span>
            )}
          </div>

          <div className="space-y-2 text-sm text-slate-600">
            <div className="flex items-center gap-2">
              <CalendarDays size={15} className="text-slate-400" />
              {fmtDate(session.startsAt)}
            </div>
            <div className="flex items-center gap-2">
              <Clock size={15} className="text-slate-400" />
              {fmtTime(session.startsAt)}
              {session.endsAt && ` – ${fmtTime(session.endsAt)}`}
            </div>
            {session.deliveryMethod === 'Online' ? (
              <div className="flex items-center gap-2">
                <Globe size={15} className="text-slate-400" />
                Online — the joining link is sent once you book
              </div>
            ) : session.venue ? (
              <div className="flex items-center gap-2">
                <MapPin size={15} className="text-slate-400" />
                {session.venue.name}
                {session.venue.city ? `, ${session.venue.city}` : ''}
                {session.venue.postcode ? ` ${session.venue.postcode}` : ''}
              </div>
            ) : (
              <div className="flex items-center gap-2 text-slate-400">
                <MapPin size={15} /> Venue to be confirmed
              </div>
            )}
            <div className="flex items-center gap-2">
              <Users size={15} className="text-slate-400" />
              {session.availablePlaces} of {session.totalPlaces} places remaining
            </div>
          </div>

          {fullyBooked && (
            <div className="mt-4 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-700 font-medium">
              This session is fully booked.
            </div>
          )}
        </div>

        {/* Booking form */}
        {!fullyBooked ? (
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h2 className="font-semibold text-slate-900 mb-4">Book your place</h2>
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Booking contact */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-2">Your details</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Full name <span className="text-red-500">*</span>
                    </label>
                    <input
                      required
                      value={contactName}
                      onChange={e => setContactName(e.target.value)}
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                      placeholder="Jane Smith"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Email address <span className="text-red-500">*</span>
                    </label>
                    <input
                      required
                      type="email"
                      value={contactEmail}
                      onChange={e => setContactEmail(e.target.value)}
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                      placeholder="jane@example.com"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Phone <span className="text-red-500">*</span>
                    </label>
                    <input
                      required
                      type="tel"
                      value={contactPhone}
                      onChange={e => setContactPhone(e.target.value)}
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                      placeholder="07700 900000"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Number of places <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={attendees.length}
                      onChange={e => setPlaces(parseInt(e.target.value))}
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    >
                      {Array.from({ length: Math.min(session.availablePlaces, 10) }, (_, i) => i + 1).map(n => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Attendees */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-1">Who's attending?</h3>
                <p className="text-xs text-slate-400 mb-3">Each attendee receives their own certificate, so we need a name and email for everyone.</p>
                <div className="space-y-4">
                  {attendees.map((a, i) => (
                    <div key={i} className="rounded-lg border border-slate-200 p-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-3">Attendee {i + 1}</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            Full name <span className="text-red-500">*</span>
                          </label>
                          <input
                            required
                            value={a.fullName}
                            onChange={e => updateAttendee(i, { fullName: e.target.value })}
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                            placeholder="Full name"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            Email address <span className="text-red-500">*</span>
                          </label>
                          <input
                            required
                            type="email"
                            value={a.email}
                            onChange={e => updateAttendee(i, { email: e.target.value })}
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                            placeholder="name@example.com"
                          />
                        </div>
                      </div>
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-slate-700 mb-1">Company</label>
                        <input
                          value={a.companyName}
                          onChange={e => updateAttendee(i, { companyName: e.target.value })}
                          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                          placeholder="Optional"
                        />
                      </div>
                      <label className="mt-3 flex items-start gap-2 text-sm text-slate-600 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={a.marketingConsent}
                          onChange={e => updateAttendee(i, { marketingConsent: e.target.checked })}
                          className="mt-0.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-400"
                        />
                        Email me about future courses and training.
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                <textarea
                  rows={2}
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder="Any requirements or questions…"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 resize-none"
                />
              </div>

              {bookMutation.isError && (
                <p className="text-sm text-red-600">
                  {(bookMutation.error as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Booking failed. Please try again.'}
                </p>
              )}

              {(() => {
                const total = calculateTotal(session.pricingModel, session.price, session.flatFeeAmount, attendees.length)
                return total !== null ? (
                  <p className="text-sm text-slate-500">
                    Total: <strong className="text-slate-800">{fmtMoney(total)}</strong>
                    {session.pricingModel === 'FlatFee'
                      ? ' (flat fee)'
                      : ` for ${attendees.length} place${attendees.length !== 1 ? 's' : ''}`}
                  </p>
                ) : null
              })()}

              {data.photosEnabled && (
                <p className="text-xs text-slate-500">
                  {tenantName} may take a photo of you at the course to confirm your identity.{' '}
                  <a href={`/privacy/photos/${slug}`} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">
                    How we use it
                  </a>
                </p>
              )}

              {requiresTerms && (
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={e => setTermsAccepted(e.target.checked)}
                    required
                    className="mt-0.5 w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-sm text-slate-600">
                    I have read and accept{' '}
                    <a href={publicTermsUrl(slug!)} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">
                      {tenantName}'s Terms &amp; Conditions
                    </a>
                  </span>
                </label>
              )}

              <Button type="submit" className="w-full" disabled={bookMutation.isPending || (requiresTerms && !termsAccepted)}>
                {bookMutation.isPending ? 'Booking…' : 'Confirm booking'}
              </Button>
            </form>
          </div>
        ) : null}
      </div>
    </div>
  )
}
